import PropTypes from 'prop-types';
import {createUseStyles, useTheme} from 'react-jss';
import {connect} from 'react-redux';
import FieldTypes from '../../../enums/FieldTypes';
import loadArtemisFonts from '../../components/ui/loadArtemisFonts';
import Translation from '../../lang/Translation';
import isUsingArtemisStyles from '../../selectors/isUsingArtemisStyles';
import Spacing from '../../style-helper/Spacing';
import Typography from '../../style-helper/Typography';
import Width from '../../style-helper/Width';
import FieldLabel from '../FieldLabel';
import ValidationMessage from '../ValidationMessage/ValidationMessage';
import FieldInfoAndMessages from './FieldInfoAndMessages/FieldInfoAndMessages';
import LabelWrapper from './LabelWrapper/LabelWrapper';

const useStyles = createUseStyles({
  label: props => ({
    marginBottom: Spacing.half,
    marginLeft: Spacing.single,
    fontSize: 14.4,
    fontWeight: 600,
    ...(props.isUsingArtemisStyles && props.theme?.typography?.[Typography.body])
    && loadArtemisFonts(props.theme?.typography[Typography.body],
      props.theme.button.isBold),
  }),
  fieldContainer: {
    display: 'flex',
  },
  fieldInnerContainer: {
    width: props => (props.fieldWidth === 2 ? Width.half : Width.full),
  },
  radioContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  radio: {
    width: 'auto',
    marginRight: Spacing.single,
  },
  
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  checkbox: {
    width: 'auto',
    marginRight: Spacing.double,
  },
});

function FieldWrapper(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  
  const renderToolTipAndLink = (
    <FieldInfoAndMessages link={props.link} toolTip={props.toolTip} label={props.label} />
  );
  
  const renderValidationMessage = (
    <ValidationMessage errorText={props.errorText} associatedFieldId={props.id} />
  );
  
  const renderMockInput = () => (
    <div
      id={props.customContainerId}
      className={`field ${props.wrappedClassName || ''} ${props.toolTip ? 'FieldWrapper--has-tooltip' : ''}`}
    >
      <div className={classes.label} data-test="FieldWrapper__mockLabel">
        <Translation>{props.label}</Translation>
      </div>
      <div className={classes.fieldContainer}>
        <div className={classes.fieldInnerContainer}>
          {props.children}
        </div>
        {renderToolTipAndLink}
      </div>
      {props.errorText && renderValidationMessage}
    </div>
  );
  
  const renderInput = () => (
    <div
      className={`field ${props.wrappedClassName || ''} ${props.toolTip ? 'FieldWrapper--has-tooltip' : ''}`}
      id={props.customContainerId}
    >
      <LabelWrapper>
        <FieldLabel
          htmlFor={props.htmlFor}
          label={props.label}
          aria-label={props.required ? `${props.label} - required` : `${props.label} - optional`}
          hideLabel={props.hideLabel}
          required={props.required}
          value={props.value}
          fixedLength={props.fixedLength}
          validationCleaner={props.validationCleaner}
          maxLength={props.maxLength}
          fieldType={props.fieldType}
          shouldNotShowFieldOptionalLabel={props.shouldNotShowFieldOptionalLabel}
        />
      </LabelWrapper>
      <div className={classes.fieldContainer}>
        <div className={classes.fieldInnerContainer}>
          {props.children}
        </div>
        {renderToolTipAndLink}
      </div>
      {props.errorText && renderValidationMessage}
    </div>
  );
  
  const renderRadioInput = () => (
    <div className={`field ${classes.radioContainer} ${props.wrappedClassName || ''}`}>
      <div className={`field-radio ${classes.radio}`}>
        {props.children}
      </div>
      <div>
        <FieldLabel
          isRadioButton
          htmlFor={props.htmlFor}
          label={props.label}
          aria-label={props.required ? `${props.label} - required` : `${props.label} - optional`}
          required={props.required}
          value={props.value}
          maxLength={props.maxLength}
          validationCleaner={props.validationCleaner}
          shouldNotShowFieldOptionalLabel={props.shouldNotShowFieldOptionalLabel}
        />
        {renderToolTipAndLink}
      </div>
      {props.errorText && renderValidationMessage}
    </div>
  );
  
  const renderCheckboxInput = () => (
    <div className={`field ${classes.checkboxContainer} ${props.wrappedClassName || ''}`}>
      <div className={classes.checkbox}>
        {props.children}
      </div>
      <div>
        <FieldLabel
          isRadioButton
          htmlFor={props.htmlFor}
          label={props.label}
          aria-label={props.required ? `${props.label} - required` : `${props.label} - optional`}
          required={props.required}
          value={props.value}
          maxLength={props.maxLength}
          validationCleaner={props.validationCleaner}
          type="checkbox"
          shouldNotShowFieldOptionalLabel={props.shouldNotShowFieldOptionalLabel}
        />
        {renderToolTipAndLink}
      </div>
      {props.errorText && renderValidationMessage}
    </div>
  );
  
  switch (props.fieldType) {
    case FieldTypes.MOCK:
      return renderMockInput();
    case 'checkbox':
      return renderCheckboxInput();
    case 'radio':
      return renderRadioInput();
    default:
      return renderInput();
  }
}

const mapStateToProps = state => ({
  isUsingArtemisStyles: isUsingArtemisStyles(state),
});

FieldWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  errorText: PropTypes.string,
  wrappedClassName: PropTypes.string,
  customContainerId: PropTypes.string,
  htmlFor: PropTypes.string,
  link: PropTypes.object,
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  toolTip: PropTypes.string,
  fieldType: PropTypes.string,
  fieldWidth: PropTypes.number,
  shouldNotShowFieldOptionalLabel: PropTypes.bool,
  isUsingArtemisStyles: PropTypes.bool,
};

export default connect(mapStateToProps)(FieldWrapper);
